/*******************************
 * This component uses jotai async derived atoms, 
 * so it requires suspense wrapper around it when used
/*******************************/
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { currentChartTypeAtom } from 'views/state';

import {
  vulnBySeverityChartDataAtom,
  waCurrentVulnCountAtom,
  severityChartLabelsAtom,
  vulnBySeverityChartDataCountAtom,
  vulnGroupsChartDataAtom,
  vulnByStatusChartDataAtom,
  statusChartLabelsAtom,
  severityFiltersAtom,
  appliedWAIdsAtom,
  isGroupingByQidVulnListingOpenAtom,
} from './state';
import BarOrDoughnutChart from '../components/BarOrDoughnutChart';
import GroupingChart from '../components/GroupingChart';
import {
  getDrillDownUrl,
  getVulnerabilityStatusValue,
  getSeverityValue,
  getSeverityFilterValues,
} from '../helper';
import { Severity, VulnerabilityStatus } from '../types';

import '../styles.css';

type Props = {
  setModalComponentData: (ModalDialogData) => void;
};

const CurrentCharts = ({ setModalComponentData }: Props) => {
  const currentChartType = useAtomValue(currentChartTypeAtom);
  const severityFilters = useAtomValue(severityFiltersAtom);
  const setCurrentVulnCount = useSetAtom(waCurrentVulnCountAtom);

  const vulnBySeverityChartData = useAtomValue(vulnBySeverityChartDataAtom);
  const vulnBySeverityChartDataCount = useAtomValue(
    vulnBySeverityChartDataCountAtom,
  );
  const severityChartLabels = useAtomValue(severityChartLabelsAtom);
  const vulnGroupsChartData = useAtomValue(vulnGroupsChartDataAtom);
  const vulnByStatusChartData = useAtomValue(vulnByStatusChartDataAtom);
  const statusChartLabels = useAtomValue(statusChartLabelsAtom);
  const selectedWAIds = useAtomValue(appliedWAIdsAtom);
  // grouping charts vulnerabilities listing dialog open/close state
  const [isGroupingByQidVulnListingOpen, setIsGroupingByQidVulnListingOpen] =
    useAtom(isGroupingByQidVulnListingOpenAtom);

  function getChartTooltipLabels() {
    const toolTipLabels: string[] = [];
    toolTipLabels.push('Webapps');
    return toolTipLabels;
  }

  function getSeverityChartsDrillDownUrl(
    label: string,
    sourceType: string,
    patchable = false,
    obsolete = false,
  ) {
    return getDrillDownUrl({
      type: 'open',
      severity: getSeverityValue(Severity[label]).toString(),
      sourceType: 'was_data',
      issueType: 'Vulnerability',
      qid: '',
      patchable: patchable,
      obsolete: obsolete,
      severity_in: undefined,
      hosts_in: selectedWAIds,
    });
  }

  function getStatusChartsDrillDownUrl(status: string, sourceType: string) {
    // handle statuses, as the labels are customized
    if (status.toLocaleLowerCase().startsWith('new')) {
      status = VulnerabilityStatus.New;
    } else if (status.toLocaleLowerCase().startsWith('reopened')) {
      status = VulnerabilityStatus.Reopened;
    } else if (status.toLocaleLowerCase().startsWith('active')) {
      status = VulnerabilityStatus.Active;
    } else if (status.toLocaleLowerCase().startsWith('fixed')) {
      status = VulnerabilityStatus.Fixed;
    } else if (status.toLocaleLowerCase().startsWith('ignored')) {
      status = VulnerabilityStatus.Ignored;
    }

    return getDrillDownUrl({
      type: getVulnerabilityStatusValue(status as VulnerabilityStatus),
      severity: '',
      sourceType: 'was_data',
      issueType: 'Vulnerability',
      qid: '',
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedWAIds,
    });
  }

  function getGroupingDrillDownUrl(qid: string, sourceType: string) {
    return getDrillDownUrl({
      type: 'open',
      severity: '',
      sourceType: sourceType,
      issueType: 'Vulnerability',
      qid: qid,
      severity_in: getSeverityFilterValues(severityFilters),
      hosts_in: selectedWAIds,
    });
  }

  function onGroupingByQidChartExpandClick(ModalDialogData) {
    if (ModalDialogData) setIsGroupingByQidVulnListingOpen(true);
    setModalComponentData(ModalDialogData);
  }

  return (
    <>
      {vulnBySeverityChartData && (
        <>
          <div className="col-md-6">
            <BarOrDoughnutChart
              chartType={currentChartType}
              title="By Severity"
              labels={severityChartLabels}
              tooltipLabels={getChartTooltipLabels()}
              datasets={vulnBySeverityChartData}
              severityFilters={severityFilters}
              total_count={vulnBySeverityChartDataCount}
              getDrillDownUrl={getSeverityChartsDrillDownUrl}
              setCurrentVulnCount={setCurrentVulnCount}
            />
          </div>
        </>
      )}
      {vulnByStatusChartData && (
        <div className="col-md-6">
          <BarOrDoughnutChart
            chartType={currentChartType}
            title="By Status"
            labels={statusChartLabels}
            tooltipLabels={['Webapps']}
            datasets={vulnByStatusChartData}
            severityFilters={Object.values(VulnerabilityStatus)}
            getDrillDownUrl={getStatusChartsDrillDownUrl}
          />
        </div>
      )}
      {vulnGroupsChartData && (
        <>
          {
            <div className="col-md-12">
              <GroupingChart
                chartType={currentChartType}
                title="Group by QIDs"
                data={vulnGroupsChartData}
                onExpandClick={(ModalDialogData) => {
                  onGroupingByQidChartExpandClick(ModalDialogData);
                }}
                severityFilters={severityFilters}
                sourceType="was_data"
                getDrillDownUrl={getGroupingDrillDownUrl}
                isVulnListingOpen={isGroupingByQidVulnListingOpen}
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default CurrentCharts;
